/**
 * Currency symbol mapping
 * Maps currency codes (lowercase) to their respective symbols
 */
export const CURRENCY_SYMBOLS: { [key: string]: string } = {
  // Major currencies
  usd: '$',
  eur: '€',
  gbp: '£',
  jpy: '¥',
  chf: 'CHF',
  cad: 'C$',
  aud: 'A$',
  nzd: 'NZ$',

  // Asian currencies
  cny: '¥',
  inr: '₹',
  krw: '₩',
  sgd: 'S$',
  hkd: 'HK$',
  thb: '฿',
  idr: 'Rp',
  myr: 'RM',
  php: '₱',
  twd: 'NT$',
  vnd: '₫',
  pkr: 'PKR',
  bdt: 'BDT',
  lkr: 'LKR',
  mmk: 'MMK',
  khr: 'KHR',
  lak: 'LAK',

  // Middle East currencies
  aed: 'د.إ',
  sar: 'ر.س',
  ils: '₪',
  try: '₺',
  qar: 'QAR',
  kwd: 'KWD',
  bhd: 'BHD',
  omr: 'OMR',
  jod: 'JOD',
  irr: 'IRR',
  iqd: 'IQD',

  // European currencies
  sek: 'kr',
  nok: 'kr',
  dkk: 'kr',
  pln: 'zł',
  czk: 'Kč',
  huf: 'Ft',
  ron: 'RON',
  bgn: 'BGN',
  hrk: 'HRK',
  rsd: 'RSD',
  uah: '₴',
  isk: 'ISK',

  // Americas currencies
  brl: 'R$',
  mxn: 'Mex$',
  ars: 'ARS',
  clp: 'CLP',
  cop: 'COP',
  pen: 'PEN',
  uyu: 'UYU',
  bob: 'BOB',
  pyg: 'PYG',

  // African currencies
  zar: 'R',
  egp: 'EGP',
  ngn: '₦',
  kes: 'KES',
  tzs: 'TZS',
  ugx: 'UGX',
  ghs: 'GHS',
  mad: 'MAD',
  tnd: 'TND',
  dzd: 'DZD',

  // Other currencies
  rub: '₽',
  kzt: 'KZT',
  uzs: 'UZS',
  azn: 'AZN',
  gel: 'GEL',
};

/**
 * Symbols that should appear before the amount (single character symbols)
 * All other currencies are considered codes and should appear after the amount
 */
const CURRENCY_SYMBOLS_BEFORE = ['$', '€', '£', '¥', '₹', '₩', '฿', 'Rp', 'RM', '₱', '₫', 'د.إ', 'ر.س', '₪', '₺', 'kr', 'zł', 'Kč', 'Ft', '₴', 'R$', 'R', '₦', '₽'];

/**
 * Get currency symbol for a given currency code
 * @param currencyCode - The currency code (e.g., 'usd', 'eur')
 * @param defaultSymbol - The default symbol to return if currency code is not found (default: '$')
 * @returns The currency symbol
 */
export function getCurrencySymbol(currencyCode: string | undefined | null, defaultSymbol: string = '$'): string {
  if (!currencyCode) {
    return defaultSymbol;
  }

  const normalizedCode = currencyCode.toLowerCase().trim();
  return CURRENCY_SYMBOLS[normalizedCode] || defaultSymbol;
}

/**
 * Check if a currency should be displayed before the amount (symbol) or after (code)
 * @param currencySymbol - The currency symbol or code
 * @returns true if currency should be displayed before the amount, false if after
 */
export function isCurrencySymbol(currencySymbol: string): boolean {
  return CURRENCY_SYMBOLS_BEFORE.includes(currencySymbol);
}

/**
 * Format currency with proper positioning and spacing
 * @param amount - The amount to format
 * @param currencySymbol - The currency symbol or code
 * @returns Formatted currency string (e.g., "$100" or "100 RON")
 */
export function formatCurrency(amount: number | string, currencySymbol: string): string {
  if (isCurrencySymbol(currencySymbol)) {
    return `${currencySymbol}${amount}`;
  } else {
    return `${amount} ${currencySymbol}`;
  }
}
